// Copyright 2025, University of Maryland, All Rights Reserved
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Alert,
  AlertDescription,
  AlertTitle,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui';
import { Plus, RefreshCw, AlertCircle } from 'lucide-react';
import { SchedulerTable } from './SchedulerTable';
import { CreateSchedulerDialog } from './CreateSchedulerDialog';
import { EditSchedulerDialog } from './EditSchedulerDialog';
import {
  listSchedulers as defaultListSchedulers,
  createScheduler as defaultCreateScheduler,
  updateScheduler as defaultUpdateScheduler,
  deleteScheduler as defaultDeleteScheduler,
} from '../api/scheduler';
import type {
  SchedulerRecord,
  CreateSchedulerRequest,
  UpdateSchedulerRequest,
} from '../api/types/scheduler';

const SCHEDULERS_KEY = ['admin-schedulers'] as const;

// API interface for test injection (vi.mock doesn't work in browser mode)
export interface SchedulerApi {
  listSchedulers: () => Promise<SchedulerRecord[]>
  createScheduler: (data: CreateSchedulerRequest) => Promise<SchedulerRecord>
  updateScheduler: (
    job_name: string,
    data: UpdateSchedulerRequest,
    org?: string | null,
    ws?: string | null,
    scope?: string | null,
  ) => Promise<SchedulerRecord>
  deleteScheduler: (
    job_name: string,
    org?: string | null,
    ws?: string | null,
    scope?: string | null,
  ) => Promise<void>
}

interface SchedulerManagerProps {
  schedulerApi?: SchedulerApi
}

export function SchedulerManager({ schedulerApi }: SchedulerManagerProps = {}) {
  // Use injected API or defaults
  const listSchedulers = schedulerApi?.listSchedulers ?? defaultListSchedulers
  const createScheduler = schedulerApi?.createScheduler ?? defaultCreateScheduler
  const updateScheduler = schedulerApi?.updateScheduler ?? defaultUpdateScheduler
  const deleteScheduler = schedulerApi?.deleteScheduler ?? defaultDeleteScheduler

  const queryClient = useQueryClient();

  const {
    data: schedulers = [],
    isLoading,
    isFetching,
    refetch,
    error: queryError,
  } = useQuery<SchedulerRecord[]>({
    queryKey: SCHEDULERS_KEY,
    queryFn: () => listSchedulers(),
    staleTime: 5 * 60_000,
  });

  const [mutationError, setMutationError] = useState<string | null>(null);
  const error = mutationError ?? (queryError instanceof Error ? queryError.message : null);

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedScheduler, setSelectedScheduler] = useState<SchedulerRecord | null>(null);

  // Delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [schedulerToDelete, setSchedulerToDelete] = useState<SchedulerRecord | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: SCHEDULERS_KEY });

  // Handle create scheduler
  const handleCreate = async (data: CreateSchedulerRequest) => {
    setMutationError(null);
    try {
      await createScheduler(data);
      await invalidate();
    } catch (err) {
      console.error('Failed to create scheduler:', err);
      throw err;
    }
  };

  // Handle edit scheduler
  const handleEdit = (scheduler: SchedulerRecord) => {
    setSelectedScheduler(scheduler);
    setEditDialogOpen(true);
  };

  const handleUpdate = async (data: UpdateSchedulerRequest) => {
    if (!selectedScheduler) return;
    setMutationError(null);
    try {
      await updateScheduler(
        selectedScheduler.job_name,
        data,
        selectedScheduler.org,
        selectedScheduler.ws,
        selectedScheduler.scope,
      );
      await invalidate();
    } catch (err) {
      console.error('Failed to update scheduler:', err);
      throw err;
    }
  };

  // Handle delete scheduler
  const handleDeleteClick = (scheduler: SchedulerRecord) => {
    setSchedulerToDelete(scheduler);
    setDeleteDialogOpen(true);
  };

  const deleteMutation = useMutation({
    mutationFn: async (scheduler: SchedulerRecord) => {
      await deleteScheduler(scheduler.job_name, scheduler.org, scheduler.ws, scheduler.scope);
    },
    onSuccess: async () => {
      await invalidate();
      setDeleteDialogOpen(false);
      setSchedulerToDelete(null);
    },
    onError: (err) => {
      console.error('Failed to delete scheduler:', err);
      setMutationError(err instanceof Error ? err.message : 'Failed to delete scheduler');
    },
  });

  const handleDeleteConfirm = () => {
    if (!schedulerToDelete) return;
    setMutationError(null);
    deleteMutation.mutate(schedulerToDelete);
  };

  const getSchedulerDisplayName = (scheduler: SchedulerRecord) => {
    const parts = [scheduler.job_name];
    if (scheduler.scope) parts.push(`SCOPE:${scheduler.scope}`);
    else if (scheduler.ws) parts.push(`WS:${scheduler.ws}`);
    else if (scheduler.org) parts.push(`ORG:${scheduler.org}`);
    return parts.join(' / ');
  };

  const isRefreshing = isFetching && !isLoading;

  return (
    <div className="text-slate-900">
      <div className="space-y-5">
        {/* Header Section */}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-[21px] font-[650] tracking-[-0.015em]">Scheduled Jobs</h1>
            <p className="mt-[5px] text-[13px] text-slate-500">
              Jobs the platform runs automatically on a recurring schedule — no one has to
              trigger them. Each row is one job and where it applies.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => { void refetch(); }}
              disabled={isRefreshing}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              size="sm"
              onClick={() => setCreateDialogOpen(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Scheduler
            </Button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Schedulers Table */}
        <SchedulerTable
          schedulers={schedulers}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          isLoading={isLoading}
        />

        {/* Create Dialog */}
        <CreateSchedulerDialog
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
          onSubmit={handleCreate}
        />

        {/* Edit Dialog */}
        <EditSchedulerDialog
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          scheduler={selectedScheduler}
          onSubmit={handleUpdate}
        />

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Scheduled Job</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete the scheduler for{' '}
                <span className="font-semibold">
                  {schedulerToDelete && getSchedulerDisplayName(schedulerToDelete)}
                </span>
                ? This action cannot be undone and the job will no longer run automatically.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteConfirm}
                disabled={deleteMutation.isPending}
                className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
